import Api from './api';
import { PageResponse } from '@/schema/page';
import {
  AdminCaseRow,
  AdminClinicDetail,
  AdminClinicRow,
  AdminOverview,
  AdminSearchHit,
  AdminUser,
  AuditLogEntry,
  CaseQuery,
} from '@/schema/admin';

/** Drops undefined/empty entries so the query string only carries active filters. */
function toParams(values: Record<string, string | number | boolean | undefined | null>) {
  const params = new URLSearchParams();
  Object.entries(values).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    params.set(key, String(value));
  });
  return params.toString();
}

const AdminApi = {
  getOverview() {
    return Api.get<AdminOverview>('/admin/overview');
  },

  getClinics(page: number, size: number, search?: string) {
    return Api.get<PageResponse<AdminClinicRow>>(`/admin/clinics?${toParams({ page, size, search })}`);
  },

  getClinic(id: string) {
    return Api.get<AdminClinicDetail>(`/admin/clinics/${id}`);
  },

  getCases(page: number, size: number, query: CaseQuery) {
    const params = toParams({
      page,
      size,
      clinicId: query.clinicId,
      status: query.status ?? undefined,
      search: query.search,
      minDaysInStage: query.minDaysInStage,
      openOnly: query.openOnly,
    });
    return Api.get<PageResponse<AdminCaseRow>>(`/admin/cases?${params}`);
  },

  /** Moves a case into the Printing stage (or back out of it after a mis-click). */
  setCasePrinting(scanId: string, printing: boolean) {
    return Api.put<AdminCaseRow>(`/admin/cases/${scanId}/print-status`, { printing });
  },

  /**
   * Bag labels PDF: one 100×50 mm page per treatment week. Returns the blob plus the
   * server-chosen filename ("stitky-zakazka-<n>.pdf") so the download can carry the
   * order number the client has no other way of knowing.
   */
  async getBagLabelsPdf(scanId: string): Promise<{ blob: Blob; filename: string }> {
    const response = await Api.axios.get(`/admin/cases/${scanId}/labels`, {
      responseType: 'blob',
    });
    const disposition: string = response.headers['content-disposition'] ?? '';
    const filename = /filename="?([^";]+)"?/.exec(disposition)?.[1] ?? `stitky-${scanId}.pdf`;
    return { blob: response.data, filename };
  },

  search(q: string) {
    return Api.get<AdminSearchHit[]>(`/admin/search?${toParams({ q })}`);
  },

  getUsers(page: number, size: number) {
    return Api.get<PageResponse<AdminUser>>(`/admin/users?${toParams({ page, size })}`);
  },

  setUserEnabled(id: string, enabled: boolean) {
    return Api.put<AdminUser>(`/admin/users/${id}/status`, { enabled });
  },

  setUserRoles(id: string, roles: string[]) {
    return Api.put<AdminUser>(`/admin/users/${id}/roles`, { roles });
  },

  /** Superadmin only, and permanent — see the backend for what survives the account. */
  deleteUser(id: string) {
    return Api.delete<void>(`/admin/users/${id}`);
  },

  getAuditLog(page: number, size: number, action?: string, targetId?: string) {
    return Api.get<PageResponse<AuditLogEntry>>(
      `/admin/audit-log?${toParams({ page, size, action, targetId })}`,
    );
  },
};

export default AdminApi;
